import { useCallback, useEffect, useRef } from 'react';

import { fg } from '@confluence/feature-gating';

import { getUnreadCommentsIntersectionObserver } from '../UnreadCommentsIntersectionObserver';
import { useUnreadCommentsActions } from './useUnreadComments';
import { useDebouncedMarkCommentsAsRead } from './useDebouncedMarkCommentsAsRead';
import { useViewedComments } from './useViewedComments';

const MARK_COMMENTS_AS_READ_DEBOUNCE_MS = 1000;

interface UseUnreadCommentsObserverSetupProps {
	pageId: string | undefined;
}

export const useUnreadCommentsObserverSetup = ({ pageId }: UseUnreadCommentsObserverSetupProps) => {
	const { updateUnreadCommentIDSet } = useUnreadCommentsActions();

	const { debouncedMarkCommentsAsRead, flushPendingComments } = useDebouncedMarkCommentsAsRead(
		MARK_COMMENTS_AS_READ_DEBOUNCE_MS,
	);

	const { updateViewedCommentsCache } = useViewedComments({ pageId: pageId || '' });

	const intersectionCallback = useCallback(
		(commentId: string) => {
			if (commentId) {
				updateUnreadCommentIDSet(new Set([commentId]), 'remove');
				debouncedMarkCommentsAsRead(commentId);
				updateViewedCommentsCache([commentId]);
			}
		},
		[updateUnreadCommentIDSet, debouncedMarkCommentsAsRead, updateViewedCommentsCache],
	);

	// Create refs to avoid stale closures in useEffect
	const intersectionCallbackRef = useRef(intersectionCallback);
	intersectionCallbackRef.current = intersectionCallback;

	const flushPendingCommentsRef = useRef(flushPendingComments);
	flushPendingCommentsRef.current = flushPendingComments;

	useEffect(() => {
		if (!fg('confluence_frontend_unread_comments_fixes') || !pageId) {
			return;
		}

		const observer = getUnreadCommentsIntersectionObserver();

		observer.registerIntersectionCallback((commentId: string) => {
			intersectionCallbackRef.current(commentId);
		});

		return () => {
			observer.disconnect();
			flushPendingCommentsRef.current();
		};
	}, [pageId]);
};
